if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        if (this.mediaObserver) {
          this.mediaObserver.disconnect();
          this.mediaObserver = null;
        }
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner')?.classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then(async (responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            // Bring in the exact same CSS the real product page uses,
            // so the popup looks identical to the product page design.
            await this.loadProductStyles(responseHTML, productElement);

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);
            this.syncTitleStyle();
            this.buildSimpleMedia();

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            this.modalContent.querySelector('product-component')?.dispatchViewEvent?.();

            super.show(opener);
          })
          .catch(() => {
            // If anything fails, just open the normal product page
            window.location.href = opener.getAttribute('data-product-url');
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner')?.classList.add('hidden');
          });
      }

      // The popup shows the title as a link (<a>) and hides the real <h1>.
      // Copy the h1's real look (colour, font, size) onto the link so it is not default-blue.
      syncTitleStyle() {
        const heading = this.modalContent.querySelector('.product__title h1');
        const link = this.modalContent.querySelector('.product__title > a');
        if (!heading || !link) return;

        const cs = window.getComputedStyle(heading);
        const props = ['color', 'font-family', 'font-size', 'font-weight', 'line-height', 'letter-spacing', 'text-transform'];
        props.forEach((prop) => {
          const value = cs.getPropertyValue(prop);
          if (value) link.style.setProperty(prop, value, 'important');
        });
      }

      // Simple, reliable product image for the popup (used on mobile via CSS).
      // The original product-page gallery is hidden on mobile because its ratio/slider CSS
      // breaks the layout inside a popup. This keeps the image in normal flow, above the options.
      buildSimpleMedia() {
        const wrapper = this.modalContent.querySelector('.product__media-wrapper');
        if (!wrapper) return;

        const gallery = wrapper.querySelector('media-gallery');
        const getFirstImage = () =>
          gallery?.querySelector('.product__media-item img') || wrapper.querySelector('img');

        const first = getFirstImage();
        if (!first) return;

        wrapper.querySelectorAll('.qa-media').forEach((node) => node.remove());

        const box = document.createElement('div');
        box.className = 'qa-media';
        const img = document.createElement('img');
        img.alt = first.getAttribute('alt') || '';
        img.src = first.currentSrc || first.getAttribute('src') || '';
        box.appendChild(img);
        wrapper.insertBefore(box, wrapper.firstChild);

        if (this.mediaObserver) this.mediaObserver.disconnect();
        if (gallery) {
          this.mediaObserver = new MutationObserver(() => {
            const current = getFirstImage();
            const src = current && (current.currentSrc || current.getAttribute('src'));
            if (src && img.getAttribute('src') !== src) img.src = src;
          });
          this.mediaObserver.observe(gallery, {
            subtree: true,
            childList: true,
            attributes: true,
            attributeFilter: ['src', 'srcset', 'class']
          });
        }
      }

      async loadProductStyles(responseHTML, productElement) {
        const sectionId = productElement.dataset.section;
        const jobs = [];

        // 1) Stylesheets (theme css files) used by the product page but not loaded on this page
        const loadedPaths = new Set(
          Array.from(document.querySelectorAll('link[rel="stylesheet"]')).map((link) => {
            try {
              return new URL(link.getAttribute('href'), window.location.href).pathname;
            } catch (e) {
              return '';
            }
          })
        );

        responseHTML.querySelectorAll('link[rel="stylesheet"]').forEach((link) => {
          const rawHref = link.getAttribute('href');
          if (!rawHref) return;

          let url;
          try {
            url = new URL(rawHref, window.location.href);
          } catch (e) {
            return;
          }

          if (!url.pathname.includes('/assets/')) return;
          if (loadedPaths.has(url.pathname)) return;
          loadedPaths.add(url.pathname);

          jobs.push(
            new Promise((resolve) => {
              const newLink = document.createElement('link');
              newLink.rel = 'stylesheet';
              newLink.href = url.href;
              newLink.onload = resolve;
              newLink.onerror = resolve;
              setTimeout(resolve, 3000);
              document.head.appendChild(newLink);
            })
          );
        });

        // 2) Inline section styles of the product page (these are scoped to the section id,
        //    so rename the id the same way preventDuplicatedIDs() does)
        if (sectionId) {
          const newId = `quickadd-${sectionId}`;
          const marker = `style[data-quick-add-styles="${newId}"]`;

          if (!document.head.querySelector(marker)) {
            const wrapper = productElement.closest('.shopify-section') || productElement.parentElement;
            const styleNodes = wrapper ? Array.from(wrapper.querySelectorAll('style')) : [];

            const css = styleNodes
              .filter((node) => !productElement.contains(node))
              .map((node) => node.textContent)
              .join('\n')
              .replaceAll(sectionId, newId);

            if (css.trim()) {
              const styleEl = document.createElement('style');
              styleEl.setAttribute('data-quick-add-styles', newId);
              styleEl.textContent = css;
              document.head.appendChild(styleEl);
            }
          }
        }

        await Promise.all(jobs);
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
